import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Button, Card, Divider, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  AccessGroupInfos,
  AccessUserInfos,
  PermissionCombination,
  UsersAndGroupsByPermissionCombination,
} from "../../../Core/Api/Types/access-models";
import { PermissionMode } from "../../../Core/Api/Types/permission-mode";
import AccessCustomChipsSummary from "../Shared/access-custom-chips-summary";
import PermissionSummary from "../Shared/permission-summary";

function getUserName(user: AccessUserInfos) {
  return `${user.firstName![0].toUpperCase()}. ${user.lastName}`.trim();
}

function getGroupName(group: AccessGroupInfos) {
  return `${group.name} (${group.count})`;
}

interface UserAccessCardBaseProps {
  isAllUsers: boolean;
  isFromParent: boolean;
  userCanSetPermissions: boolean;
  userCanDeletePermissions: boolean;
  onEdit: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDelete: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onMouseEnterCard?: () => void;
  onMouseLeaveCard?: () => void;
}

interface UserAccessCardCombinationProps extends UserAccessCardBaseProps, UsersAndGroupsByPermissionCombination {
  isAllUsers: false;
}

interface UserAccessCardAllUsersProps extends UserAccessCardBaseProps {
  isAllUsers: true;
  permissionCombination: PermissionCombination;
}

type UserAccessCardProps = UserAccessCardCombinationProps | UserAccessCardAllUsersProps;

const isAllUsersProps = (props: UserAccessCardProps): props is UserAccessCardAllUsersProps => {
  return props.isAllUsers === true;
};

const isCombinationProps = (props: UserAccessCardProps): props is UserAccessCardCombinationProps => {
  return props.isAllUsers === false;
};

const UserAccessCard = (props: UserAccessCardProps) => {
  const { t } = useTranslation();
  const {
    isFromParent,
    permissionCombination,
    userCanSetPermissions,
    userCanDeletePermissions,
    onEdit,
    onDelete,
    onMouseEnterCard,
    onMouseLeaveCard,
  } = props;

  const [isHovered, setIsHovered] = React.useState(false);

  const totalUsersCountText = React.useMemo(() => {
    if (isAllUsersProps(props)) {
      return t("common:defaultPermissions");
    } else if (isCombinationProps(props)) {
      const totalUsersCount = props.users.length + props.groups.reduce((acc, group) => acc + group.count, 0);
      return t("component:body.userCount", { count: totalUsersCount });
    }

    return 0;
  }, [props]);

  const handleOnMouseEnter = () => {
    setIsHovered(true);
    onMouseEnterCard && onMouseEnterCard();
  };

  const handleOnMouseLeave = () => {
    setIsHovered(false);
    onMouseLeaveCard && onMouseLeaveCard();
  };

  return (
    <StyledCard onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave} className="no-hover">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack>
          <Typography variant="body1" fontWeight={500}>
            {totalUsersCountText}
          </Typography>
          {isFromParent && (
            <Typography variant="body1" color="text.tier" mt={0.5}>
              {t("page:body.fromParent")}
            </Typography>
          )}
        </Stack>
        {(userCanSetPermissions || userCanDeletePermissions) && (
          <Stack
            direction="row"
            spacing={1}
            visibility={isHovered ? "visible" : "hidden"}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            {userCanSetPermissions && (
              <Button onClick={onEdit}>
                <EditOutlinedIcon />
              </Button>
            )}
            {userCanDeletePermissions && (
              <Button onClick={onDelete}>
                <DeleteOutlinedIcon color="error" />
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      <Stack spacing={0.5}>
        {isAllUsersProps(props) && (
          <AccessCustomChipsSummary labels={[t("page:accessCustomerChipsSummary.label.allPlatformUsers")]} />
        )}
        {isCombinationProps(props) && (
          <>
            <AccessCustomChipsSummary labels={props.users.map(getUserName)} nbToShow={3} />
            <AccessCustomChipsSummary
              labels={props.groups.map(getGroupName)}
              nbToShow={3}
              chipsIcon={<GroupOutlinedIcon />}
            />
          </>
        )}
      </Stack>
      <Divider sx={{ mt: 1.5, mx: -1.5 }} />
      <PermissionSummary
        selectedPermissions={permissionCombination
          .filter((perm) => perm.mode === PermissionMode.Allow)
          .map((perm) => perm.permission)}
        variant="accent"
        showTooltip
      />
    </StyledCard>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  position: "relative",
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
  width: "100%",
  borderRadius: 8,
  backgroundColor: "transparent",
  border: "1px solid",
  borderColor: theme.palette.backgroundAlpha.border,
  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
    transition: theme.transitions.create(["background"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

export default UserAccessCard;
